import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

const initialState = {
  listeners: [],
  loading: false,
  error: null,
};

export const fetchListeners = createAsyncThunk(
  'listeners/fetchListeners',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/listeners');
      return response.data.listeners;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch listeners' });
    }
  }
);

export const createListener = createAsyncThunk(
  'listeners/createListener',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/listeners', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to create listener' });
    }
  }
);

export const startListener = createAsyncThunk(
  'listeners/startListener',
  async (listenerId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/listeners/${listenerId}/start`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to start listener' });
    }
  }
);

export const stopListener = createAsyncThunk(
  'listeners/stopListener',
  async (listenerId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/listeners/${listenerId}/stop`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to stop listener' });
    }
  }
);

export const deleteListener = createAsyncThunk(
  'listeners/deleteListener',
  async (listenerId, { rejectWithValue }) => {
    try {
      await api.delete(`/listeners/${listenerId}`);
      return listenerId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to delete listener' });
    }
  }
);

const listenersSlice = createSlice({
  name: 'listeners',
  initialState,
  reducers: {
    listenerUpdated: (state, action) => {
      const index = state.listeners.findIndex(l => l.listener_id === action.payload.listener_id);
      if (index !== -1) {
        state.listeners[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchListeners.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchListeners.fulfilled, (state, action) => {
        state.loading = false;
        state.listeners = action.payload;
      })
      .addCase(fetchListeners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
      })
      .addCase(createListener.fulfilled, (state, action) => {
        state.listeners.unshift(action.payload);
      })
      .addCase(startListener.fulfilled, (state, action) => {
        const index = state.listeners.findIndex(l => l.listener_id === action.payload.listener_id);
        if (index !== -1) {
          state.listeners[index] = action.payload;
        }
      })
      .addCase(stopListener.fulfilled, (state, action) => {
        const index = state.listeners.findIndex(l => l.listener_id === action.payload.listener_id);
        if (index !== -1) {
          state.listeners[index] = action.payload;
        }
      })
      .addCase(deleteListener.fulfilled, (state, action) => {
        state.listeners = state.listeners.filter(l => l.listener_id !== action.payload);
      });
  },
});

export const { listenerUpdated } = listenersSlice.actions;
export default listenersSlice.reducer;
